import { FC } from "react";

import {GetServerSideProps, InferGetServerSidePropsType as Infer} from "next";
import { useTranslation } from "next-i18next";

import LayoutHead from "@components/PageHeader/LayoutHead";
import withPropsMiddleware from "@lib/withPropsMiddleware";
import { loadFeatures } from "@modules/features";
import Layout, { TWithLayout } from "@modules/layout";
import { loadAffiliateData, loadStaticOrderData, OrderPage, OrderStep } from "@modules/order";
import { QUERY_PARAMS } from "@utils/constants";
import type {TStaticInitialOrderData} from "@modules/order/types";
import loadWeightRegulations from "@modules/weight-regulations/services/loadWeightRegulations";

type TWithLocales = {
    defaultLocale: string;
    customLocales: string[];
    customLocale: string; // Change from string? to string
    features: string[] | null;
    orderData: TStaticInitialOrderData;
    weightRegulations: string[] | null;
};

export const getServerSideProps: GetServerSideProps<TWithLocales> = withPropsMiddleware(
  async ({
    locale,
    req: {
      headers: { host },
    },
    query,
  }) => {
    const affiliateId = query[QUERY_PARAMS.ORDER_AFFILIATE_ID] as string;
    const initialAffiliateData = affiliateId ? await loadAffiliateData(affiliateId, locale!, host!) : null;

    const isScrollToReceiverInfo = query[QUERY_PARAMS.ORDER_RECEIVER_INFO] === "true";

    const orderDataPromise = loadStaticOrderData({
      initialAffiliateData,
      isScrollToReceiverInfo,
      locale: locale!,
      hostname: host!,
    });

    const [orderData] = await Promise.all([orderDataPromise]);

    const features = locale ? await loadFeatures(locale, host) : null;

    const weightRegulations = locale? await loadWeightRegulations(locale, host) : null;

    return {
      props: { orderData, features, defaultLocale: '', customLocales: [], customLocale: '', weightRegulations  },
    };
  }
);

const OrderShipmentPage: TWithLayout<FC<Infer<typeof getServerSideProps>>> = ({ orderData, weightRegulations }) => {
  const { t } = useTranslation();

  return (
    <>
      <LayoutHead title={t("pages-order_shipment-title")} description={t("pages-order_shipment-description")} />
      <OrderPage currentStep={OrderStep.ShipmentDetails} orderData={orderData} weightRegulations={weightRegulations} />
    </>
  );
};

OrderShipmentPage.Layout = <Layout hasMobileFooter={false} />;

export default OrderShipmentPage;
